import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { isLocalEnvironment } from '../config/environment.js'

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      user: null,
      token: null,
      _hasHydrated: false,
      
      // Actions
      login: (token, user) => {
        set({
          isAuthenticated: true,
          user,
          token,
        })
      },
      
      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          token: null,
        })
      },
      
      updateUser: (userData) => {
        set((state) => ({
          user: { ...state.user, ...userData }
        }))
      },
      
      // Getters
      getToken: () => get().token,
      getUser: () => get().user,
      isAdmin: () => get().user?.is_admin || false,
      
      // Check if token is expired
      isTokenValid: () => {
        const token = get().token
        if (!token) return false
        
        // Accept development auto-login token
        if (token === "dev-token-valid-auto-login") {
          console.log('Local development: accepting auto-login token')
          return true
        }
        
        try {
          // Decode JWT payload (basic check without verification)
          const payload = JSON.parse(atob(token.split('.')[1]))
          const now = Math.floor(Date.now() / 1000)
          
          // In local environments, provide more lenient validation for development
          if (isLocalEnvironment()) {
            // Allow tokens to be valid for 24 hours in local development
            const hoursSinceIssued = payload.iat ? (now - payload.iat) / 3600 : 0
            const isRecentToken = hoursSinceIssued < 24
            
            if (isRecentToken) {
              console.log('Local development: accepting recent token')
              return true
            }
          }
          
          const isValid = payload.exp > now
          console.log('Token validation:', { exp: payload.exp, now, valid: isValid })
          return isValid
        } catch (error) {
          console.log('Token validation error:', error)
          return false
        }
      },
      
      // Initialize auth state
      initialize: () => {
        const state = get()
        console.log('Auth store initialize called, current state:', {
          isAuthenticated: state.isAuthenticated,
          hasUser: !!state.user,
          hasToken: !!state.token,
          _hasHydrated: state._hasHydrated
        })
        
        // DEVELOPMENT AUTO-LOGIN - ENABLED FOR TESTING
        if ((window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && 
            (!state.isAuthenticated || !state.user || !state.token)) {
          console.log('Development mode: Auto-logging in as admin')
          set({
            isAuthenticated: true,
            user: {
              id: 999,
              email: "mpogue@zenstarstudio.com",
              first_name: "Mike",
              last_name: "Pogue",
              role: "admin",
              is_admin: true
            },
            token: "eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwOi8vbG9jYWxob3N0OjgwMDAiLCJhdWQiOiJodHRwOi8vbG9jYWxob3N0OjgwMDAiLCJpYXQiOjE3NTAwNDk5NDQsImV4cCI6MTc1MDA2NDM0NCwidXNlcl9pZCI6OTk5LCJlbWFpbCI6Im1wb2d1ZUB6ZW5zdGFyc3R1ZGlvLmNvbSIsImlzX2FkbWluIjp0cnVlLCJyb2xlIjoiYWRtaW4ifQ.1LgQBENRn8IG_Qj7it4Kr3b85sqZWcjHuwRomCvhnkU"
          })
          return
        }
        
        if (state.token && !state.isTokenValid()) {
          console.log('Token expired, logging out')
          state.logout()
        } else if (state.token && state.user) {
          console.log('Valid auth state found')
          // Ensure isAuthenticated is true if we have valid token and user
          if (!state.isAuthenticated) {
            set({ isAuthenticated: true })
          }
        }
      },
      
      // Mark as hydrated
      setHasHydrated: (hasHydrated) => {
        set({ _hasHydrated: hasHydrated })
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        console.log('Auth store rehydrated from localStorage:', state)
        state?.setHasHydrated(true)
      }
    }
  )
)

export default useAuthStore
